import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, ArrowLeft, FileText, Send, X, Paperclip, AlertTriangle, CheckCircle2, Clock4, RefreshCw, XCircle, Upload } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Button, Field, Input, Select, Badge, Modal } from "@/components/ui-kit";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
};
const todayStr = () => new Date().toISOString().slice(0, 10);

const STATUS_TONE = { pending: "amber", approved: "emerald", rejected: "red", cancelled: "stone" };
const STATUS_LABEL = { pending: "Menunggu", approved: "Disetujui", rejected: "Ditolak", cancelled: "Dibatalkan" };

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Iter v34e (#1) — Karyawan portal page untuk mengajukan & melihat status cuti/sakit/izin.
 */
export default function KaryawanCuti() {
  const [data, setData] = useState({ rows: [], quota: null });
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({
    category: "cuti_tahunan", start_date: todayStr(), end_date: todayStr(),
    reason: "", attachment_b64: "", attachment_filename: "", attachment_mime: "",
  });
  const [busy, setBusy] = useState(false);
  const year = new Date().getFullYear();

  const load = async () => {
    setLoading(true);
    try {
      const [reqs, hols] = await Promise.all([
        api.get(`/leave-requests/me?year=${year}`),
        api.get(`/national-holidays?year=${year}`).catch(() => ({ data: { rows: [] } })),
      ]);
      setData(reqs.data);
      setHolidays(hols.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data cuti");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const daysCount = useMemo(() => {
    try {
      const a = new Date(form.start_date), b = new Date(form.end_date);
      const ms = b - a;
      if (Number.isNaN(ms) || ms < 0) return 0;
      return Math.floor(ms / 86400000) + 1;
    } catch { return 0; }
  }, [form.start_date, form.end_date]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5_000_000) return toast.error("File maksimal 5MB");
    try {
      const b64 = await fileToBase64(f);
      setForm((p) => ({ ...p, attachment_b64: b64, attachment_filename: f.name, attachment_mime: f.type || "application/octet-stream" }));
    } catch { toast.error("Gagal baca file"); }
  };

  const submit = async () => {
    if (!form.reason.trim()) return toast.error("Alasan wajib diisi");
    if (form.category === "sakit" && !form.attachment_b64) {
      if (!window.confirm("Pengajuan sakit tanpa surat dokter. Lanjutkan?")) return;
    }
    setBusy(true);
    try {
      await api.post("/leave-requests", form);
      toast.success("Pengajuan terkirim. Menunggu approval HR.");
      setOpenForm(false);
      setForm({ category: "cuti_tahunan", start_date: todayStr(), end_date: todayStr(), reason: "",
                attachment_b64: "", attachment_filename: "", attachment_mime: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal kirim pengajuan");
    } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Batalkan pengajuan cuti ini?")) return;
    try {
      await api.post(`/leave-requests/${id}/cancel`);
      toast.success("Dibatalkan");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal batalkan");
    }
  };

  const quota = data?.quota?.quotas || {};

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8 space-y-6" data-testid="karyawan-cuti-page">
      <div className="flex items-start justify-between">
        <PageHeader overline="Karyawan" title="Cuti & Izin Saya"
          description={`Ringkasan kuota tahun ${year} & riwayat pengajuan. Pengajuan harus disetujui HR.`} />
        <Button variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>

      <Link to="/karyawan/absen" className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Kembali ke Absensi
      </Link>

      {/* Quota cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cuti-quota">
        {["cuti_tahunan", "cuti_khusus", "sakit", "izin"].map((k) => {
          const q = quota[k] || { remaining: 0, total: 0, used: 0, label: k };
          const pct = q.total > 0 ? Math.min(100, (q.used / q.total) * 100) : 0;
          const tone = q.remaining === 0 ? "border-red-400" : q.remaining < 3 ? "border-amber-400" : "border-emerald-400";
          return (
            <Card key={k} className={`p-4 border-l-4 ${tone}`} data-testid={`quota-${k}`}>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">{q.label}</div>
              <div className="text-2xl font-display font-bold text-stone-900">{q.remaining}<span className="text-sm font-normal text-stone-500"> /{q.total} hari</span></div>
              <div className="mt-2 bg-stone-100 h-1.5 overflow-hidden">
                <div className={`h-1.5 ${q.remaining === 0 ? "bg-red-400" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[11px] text-stone-500 mt-1">Terpakai: {q.used} hari</div>
            </Card>
          );
        })}
      </div>

      <Button variant="primary" onClick={() => setOpenForm(true)} data-testid="cuti-new"><Send className="h-3.5 w-3.5" /> Ajukan Cuti / Sakit / Izin</Button>

      {/* Iter v34f (#2) — Upcoming national holidays for awareness */}
      {holidays.length > 0 && (
        <Card className="p-4" data-testid="karyawan-holidays">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-2">
            <CalendarCheck className="h-3 w-3" /> Libur Nasional &amp; Cuti Bersama {year}
          </div>
          <div className="flex flex-wrap gap-2">
            {holidays.slice(0, 12).map((h) => (
              <span key={h.id} className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] border ${h.is_cuti_bersama ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} title={h.name}>
                <b>{new Date(h.date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" })}</b>
                <span className="text-stone-600">·</span>
                {h.name}
              </span>
            ))}
            {holidays.length > 12 && (
              <span className="text-[11px] text-stone-500 self-center">+{holidays.length - 12} lainnya</span>
            )}
          </div>
        </Card>
      )}

      {/* History list */}
      <Card className="p-0 overflow-x-auto" data-testid="cuti-list">
        <div className="px-4 py-3 border-b border-stone-100">
          <h3 className="font-display font-semibold text-sm text-stone-900 flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-stone-500" /> Riwayat Pengajuan {year}</h3>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-600">
            <tr>
              <th className="text-left px-3 py-2.5">Diajukan</th>
              <th className="text-left px-3 py-2.5">Jenis</th>
              <th className="text-left px-3 py-2.5">Periode</th>
              <th className="text-right px-3 py-2.5">Hari</th>
              <th className="text-left px-3 py-2.5">Alasan</th>
              <th className="text-center px-3 py-2.5">Status</th>
              <th className="text-center px-3 py-2.5">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading && <tr><td colSpan={7} className="px-3 py-12 text-center text-xs text-stone-400">Memuat...</td></tr>}
            {!loading && data.rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-12 text-center text-xs text-stone-400">Belum ada pengajuan.</td></tr>
            )}
            {data.rows.map((r) => (
              <tr key={r.id} className="hover:bg-stone-50/40" data-testid={`cuti-row-${r.id}`}>
                <td className="px-3 py-2.5 font-mono text-[11px]">{fmtDate(r.submitted_at)}</td>
                <td className="px-3 py-2.5">{r.category_label}</td>
                <td className="px-3 py-2.5">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</td>
                <td className="px-3 py-2.5 text-right font-mono">{r.days_count}</td>
                <td className="px-3 py-2.5 max-w-[220px] truncate" title={r.reason}>
                  {r.reason}
                  {r.has_attachment && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/leave-requests/${r.id}/attachment`} target="_blank" rel="noreferrer" className="ml-1 text-terra-700" title="Lihat lampiran">
                      <Paperclip className="h-3 w-3 inline" />
                    </a>
                  )}
                  {r.review_note && r.status !== "pending" && (
                    <div className="text-[10px] text-stone-500 italic mt-0.5">Note HR: {r.review_note}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-center">
                  <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                </td>
                <td className="px-3 py-2.5 text-center">
                  {r.status === "pending" && (
                    <button onClick={() => cancel(r.id)} className="text-red-600 hover:underline text-[11px] inline-flex items-center gap-1" data-testid={`cuti-cancel-${r.id}`}>
                      <X className="h-3 w-3" /> Batal
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Form modal */}
      <Modal open={openForm} onClose={() => setOpenForm(false)} title="Ajukan Cuti / Sakit / Izin" size="md">
        <div className="space-y-4" data-testid="cuti-form">
          <Field label="Jenis Pengajuan *">
            <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="cuti-form-category">
              <option value="cuti_tahunan">Cuti Tahunan</option>
              <option value="cuti_khusus">Cuti Khusus</option>
              <option value="sakit">Sakit (boleh lampirkan surat dokter)</option>
              <option value="izin">Izin</option>
            </Select>
            <div className="text-[11px] text-stone-500 mt-1">
              Sisa kuota {form.category.replace("_", " ")}: <b>{quota[form.category]?.remaining ?? 0}</b> / {quota[form.category]?.total ?? 0} hari
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tanggal Mulai *">
              <DatePicker value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="cuti-form-start" />
            </Field>
            <Field label="Tanggal Akhir *">
              <DatePicker value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="cuti-form-end" />
            </Field>
          </div>
          <div className="bg-stone-50 border border-stone-200 px-3 py-2 text-xs">
            Total hari pengajuan: <b>{daysCount} hari</b>
            {daysCount > (quota[form.category]?.remaining ?? 0) && (
              <span className="text-red-600 ml-2"><AlertTriangle className="h-3 w-3 inline" /> Melebihi kuota</span>
            )}
          </div>
          <Field label="Alasan / Keperluan *">
            <textarea
              rows={3}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="w-full border border-stone-300 px-3 py-2 text-sm"
              data-testid="cuti-form-reason"
              placeholder="Jelaskan alasan pengajuan..."
            />
          </Field>
          {form.category === "sakit" && (
            <Field label="Lampiran Surat Dokter (opsional, gambar/PDF, ≤5MB)">
              <label className="flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50" data-testid="cuti-form-attach-label">
                <Upload className="h-3.5 w-3.5" /> {form.attachment_filename || "Pilih file"}
                <input type="file" className="hidden" accept="image/*,application/pdf" onChange={onFile} data-testid="cuti-form-attach" />
              </label>
            </Field>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
            <Button variant="outline" onClick={() => setOpenForm(false)}>Batal</Button>
            <Button variant="primary" onClick={submit} disabled={busy || daysCount <= 0 || daysCount > (quota[form.category]?.remaining ?? 0)} data-testid="cuti-form-submit">
              <Send className="h-3.5 w-3.5" /> {busy ? "Mengirim..." : "Kirim Pengajuan"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
